import { useId } from 'react';
import type {
  MiceCandidate,
  MiceCheck,
  MiceChosenFlight,
  MiceDirectionDecision,
  MiceEventWindowFlight,
  MiceMoney,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { ToneChip } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';
import { CheckList } from './check_list';
import { formatFlights, formatLegTimes, formatMoney } from './need_format';
import type { CardPick } from './need_mark';

type CardKind = 'requested' | 'agent' | 'kept' | 'eventWindow' | 'listed';

const TITLE = {
  requested: 'mice.card.requested',
  agent: 'mice.card.agent',
  kept: 'mice.card.kept',
  eventWindow: 'mice.card.eventWindow',
  listed: 'mice.card.listed',
} as const satisfies Record<CardKind, string>;

interface Card {
  kind: CardKind;
  flight: MiceCandidate;
  checks: MiceCheck[];
  why: string | null;
  /** Os motivos de o pedido ferir as regras do evento. */
  note: string | null;
  /** O pedido fere as regras do evento: marcá-lo emite fora delas. */
  breaksRules: boolean;
  savings: MiceMoney | null;
}

/** Amarelos de horário que a frase do porquê lembra de conferir. */
const SCHEDULE_YELLOW = ['leaves_before_end', 'arrives_during_event'] as const;

/** A economia como o servidor a escreve nos checks ("520,00"); sem o check, formatada aqui. */
function savingsText(decision: MiceDirectionDecision): string {
  const fromCheck = decision.checks.chosen.find((c) => c.key === 'price')?.values.savings;
  if (fromCheck) return fromCheck;
  if (!decision.savings) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(
    decision.savings.amount,
  );
}

/** A frase do porquê do sentido: a decisão e, quando há, o amarelo de horário a conferir. */
function whyText(decision: MiceDirectionDecision, chosen: MiceChosenFlight | null): string {
  if (!chosen || !decision.decision) return '';
  const base = t(`mice.why.${decision.decision}`, { savings: savingsText(decision) });
  const schedule = SCHEDULE_YELLOW.filter((y) => decision.yellow.includes(y)).map((y) =>
    t(`mice.why.${y}`),
  );
  return [base, ...schedule].join(' ');
}

const card = (kind: CardKind, flight: MiceCandidate, extra: Partial<Card> = {}): Card => ({
  kind,
  flight,
  checks: [],
  why: null,
  note: null,
  breaksRules: false,
  savings: null,
  ...extra,
});

/** O voo trazido da lista: o que fere uma regra do evento diz qual, como o voo pedido (#5917). */
function listedCard(flight: MiceEventWindowFlight): Card {
  const breaks = flight.rulesBroken.length > 0;
  return card('listed', flight, {
    breaksRules: breaks,
    note: breaks ? t('mice.card.breaksRules', { reasons: flight.rulesBroken.join('; ') }) : null,
  });
}

/** O pedido do viajante (quando encontrado ou fora das regras) e a sugestão do agente, se outra. */
function requestedAndAgent(decision: MiceDirectionDecision, chosen: MiceChosenFlight | null) {
  const why = whyText(decision, chosen);
  const { requested, requestedStatus, checks } = decision;
  const cards: Card[] = [];
  if (requested && (requestedStatus === 'found' || requestedStatus === 'breaks_rules')) {
    const isChosen = requested.id === chosen?.id;
    const breaks = requestedStatus === 'breaks_rules';
    cards.push(
      card('requested', requested, {
        checks: isChosen ? checks.chosen : checks.requested,
        breaksRules: breaks,
        why: isChosen ? why : null,
        note: breaks
          ? t('mice.card.breaksRules', { reasons: decision.requestedReasons.join('; ') })
          : null,
      }),
    );
  }
  if (chosen && chosen.id !== requested?.id) {
    cards.push(card('agent', chosen, { checks: checks.chosen, why, savings: decision.savings }));
  }
  return cards;
}

/** Os cartões de um sentido; sem decisão (recomendação antiga), só o voo do agente, sem premissas. */
function suggestionCards(
  decision: MiceDirectionDecision | null,
  chosen: MiceChosenFlight | null,
): Card[] {
  if (!decision) return chosen ? [card('agent', chosen)] : [];
  const kept = decision.decision === 'kept' ? (chosen ?? decision.requested) : null;
  const cards = kept
    ? [card('kept', kept, { checks: decision.checks.chosen, why: whyText(decision, chosen) })]
    : requestedAndAgent(decision, chosen);
  const window = decision.eventWindowOption;
  if (window && window.id !== chosen?.id) cards.push(card('eventWindow', window));
  return cards;
}

/** O voo cotado tem o preço da tarifa; o da busca, o preço da busca. */
const priceOf = (flight: MiceCandidate | MiceChosenFlight): MiceMoney =>
  'farePrice' in flight ? flight.farePrice : flight.price;

/** O porquê do pedido fora das regras (e o aviso quando ele está marcado) e a origem do voo da lista. */
function CardNotes({ card: c, picked }: Readonly<{ card: Card; picked: boolean }>) {
  return (
    <>
      {c.note ? <span className="text-micro text-destructive">{c.note}</span> : null}
      {c.breaksRules && picked ? (
        <span className="rounded-md bg-tone-amber-tint px-2 py-1 text-micro text-tone-amber-ink">
          {t('mice.card.breaksRulesPicked')}
        </span>
      ) : null}
      {c.kind === 'listed' ? (
        <span className="text-micro text-muted-foreground">{t('mice.card.listedNote')}</span>
      ) : null}
    </>
  );
}

interface FlightCardProps {
  card: Card;
  name: string;
  selected: boolean;
  readOnly: boolean;
  onSelect: () => void;
}

function FlightCard({ card: c, name, selected, readOnly, onSelect }: Readonly<FlightCardProps>) {
  const title = t(TITLE[c.kind]);
  const radio = !readOnly;
  const Box = radio ? 'label' : 'div';
  const savings = c.savings && c.savings.amount > 0 ? c.savings : null;
  return (
    <Box
      className={twcn(
        'flex min-w-0 flex-col gap-1 rounded-lg border bg-background p-3',
        radio && 'cursor-pointer',
        selected ? 'border-primary shadow-ring-primary' : 'border-border',
      )}
    >
      <span className="flex items-center gap-2">
        <span
          className={twcn(
            'flex-1 font-mono text-tiny font-medium uppercase tracking-eyebrow',
            c.kind === 'requested' || c.kind === 'eventWindow'
              ? 'text-muted-foreground'
              : 'text-primary',
          )}
        >
          {title}
        </span>
        {radio ? (
          <input
            type="radio"
            name={name}
            aria-label={title}
            checked={selected}
            onChange={onSelect}
            className="size-4 cursor-pointer accent-primary"
          />
        ) : null}
      </span>
      <span className="tabular-nums text-foreground">
        {formatLegTimes(c.flight)}{' '}
        <span className="text-micro text-muted-foreground">
          {c.flight.stops === 0
            ? t('mice.card.direct')
            : t('mice.card.stops', { count: c.flight.stops })}
        </span>
      </span>
      <span className="font-mono text-micro text-muted-foreground">
        {`${formatFlights(c.flight)} · ${c.flight.from} → ${c.flight.to}`}
      </span>
      <span className="flex items-center gap-2 font-medium tabular-nums text-foreground">
        {formatMoney(priceOf(c.flight))}
        {savings ? (
          <ToneChip tone="green">
            {t('mice.leg.savings', { amount: formatMoney(savings) })}
          </ToneChip>
        ) : null}
      </span>
      <CardNotes card={c} picked={selected && !readOnly} />
      <CheckList checks={c.checks} />
      {c.why ? (
        <p className="border-t border-dashed border-border-soft pt-1 text-meta text-foreground">
          {c.why}
        </p>
      ) : null}
    </Box>
  );
}

/** Os voos que já têm cartão no sentido: escolher um deles na lista marca o próprio cartão. */
export function suggestionFlights(
  decision: MiceDirectionDecision | null,
  chosen: MiceChosenFlight | null,
): MiceCandidate[] {
  return suggestionCards(decision, chosen).map((c) => c.flight);
}

/**
 * Os cartões de um sentido lado a lado: o pedido do viajante (também o que fere as regras do
 * evento), a sugestão do agente (ou o pedido mantido), a opção dentro do horário do evento e o voo
 * trazido da lista da faixa do evento. O marcado tem borda; `readOnly` tira os radios.
 */
export function DirectionCards({
  decision,
  chosen,
  listed,
  selected,
  onSelect,
  readOnly,
}: Readonly<{
  /** `null`: recomendação anterior às decisões por sentido. */
  decision: MiceDirectionDecision | null;
  chosen: MiceChosenFlight | null;
  listed: MiceEventWindowFlight | null;
  selected: CardPick | undefined;
  onSelect: (pick: CardPick) => void;
  readOnly: boolean;
}>) {
  const name = useId();
  const cards: Array<Card & { source: CardPick['source'] }> = [
    ...suggestionCards(decision, chosen).map((c) => ({ ...c, source: 'suggestion' as const })),
    ...(listed ? [{ ...listedCard(listed), source: 'event_list' as const }] : []),
  ];
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {decision?.requestedStatus === 'not_found' ? (
        <div className="flex items-center rounded-lg border border-dashed border-border p-3 text-meta text-muted-foreground">
          {t('mice.card.notFound')}
        </div>
      ) : null}
      {cards.map((c) => (
        <FlightCard
          key={`${c.kind}:${c.flight.id}`}
          card={c}
          name={name}
          selected={selected?.source === c.source && selected.id === c.flight.id}
          readOnly={readOnly}
          onSelect={() => {
            onSelect({ id: c.flight.id, source: c.source });
          }}
        />
      ))}
    </div>
  );
}
